import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Activity, Check, Copy, ExternalLink, Info, Link2, Loader2,
    MousePointerClick, PlusCircle, RefreshCw, Rocket, ShieldCheck,
    TrendingUp, Wallet, X,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { useInsideV12UserLayout } from "@/components/user_v12/V12UserLayout";
import { TierProgressCard } from "@/components/TierProgressCard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";


function KpiCard({ icon: Icon, label, value, hint, tone = "primary", testid }) {
    const toneCls = {
        primary: "text-[var(--color-primary)]",
        secondary: "text-[var(--color-secondary)]",
        success: "text-[var(--color-success)]",
        warning: "text-neutral-400",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneCls}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
            {hint && (
                <p className="text-[10px] text-text-secondary mt-1 leading-tight">{hint}</p>
            )}
        </div>
    );
}


function ConversionStatusBadge({ status }) {
    const cls = {
        pending: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
        pending_review: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
        approved: "text-[var(--color-secondary)] bg-[var(--color-secondary)]/10 border-[var(--color-secondary)]/40",
        approved_pending_payout: "text-[var(--color-secondary)] bg-[var(--color-secondary)]/10 border-[var(--color-secondary)]/40",
        paid: "text-[var(--color-success)] bg-[var(--color-success)]/10 border-[var(--color-success)]/40",
        rejected: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
        fraud_hold: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
    }[status] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {(status || "").replace(/_/g, " ")}
        </span>
    );
}


function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`relative text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2 ${
                active
                    ? "border-[var(--color-primary)] text-text-main bg-[var(--color-primary)]/10"
                    : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
            }`}
        >
            <Icon className="h-3 w-3" /> {label}
            {typeof count === "number" && (
                <span className="ml-1 text-[10px] tabular-nums opacity-70">({count})</span>
            )}
        </button>
    );
}


function TABS() {
    return [
        { key: "links", label: "My links", icon: Link2 },
        { key: "clicks", label: "Clicks", icon: MousePointerClick },
        { key: "conversions", label: "Conversions", icon: TrendingUp },
    ];
}


function copyToClipboard(text) {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text)
            .then(() => toast.success("Copied to clipboard"))
            .catch(() => toast.error("Copy failed"));
    } else {
        const ta = document.createElement("textarea");
        ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); toast.success("Copied to clipboard"); }
        catch { toast.error("Copy failed"); }
        finally { document.body.removeChild(ta); }
    }
}


function fmtEur(v) {
    const n = Number(v || 0);
    return `$${n.toFixed(2)}`;
}


function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}


export default function AffiliateDashboardV2() {
    const { user: me } = useAuth();
    const insideV12 = useInsideV12UserLayout();  // MP.7 Hotfix R2 · skip legacy chrome if parent V12UserLayout
    const [tab, setTab] = useState("links");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [kpis, setKpis] = useState({});
    const [links, setLinks] = useState([]);
    const [clicks, setClicks] = useState([]);
    const [conversions, setConversions] = useState([]);
    const [disabled, setDisabled] = useState(false);

    // create-link modal
    const [showCreate, setShowCreate] = useState(false);
    const [landingUrl, setLandingUrl] = useState("");
    const [campaign, setCampaign] = useState("");
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            const [dash, lk, ck, cv] = await Promise.all([
                api.affV2Dashboard(),
                api.affV2ListLinks(100),
                api.affV2ListClicks(100),
                api.affV2ListConversions(100),
            ]);
            setKpis(dash || {});
            setLinks(lk?.items || []);
            setClicks(ck?.items || []);
            setConversions(cv?.items || []);
            setDisabled(false);
        } catch (e) {
            if (e.status === 503) {
                setDisabled(true);
            } else {
                toast.error(e.message || "Failed to load");
            }
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await load();
            setLoading(false);
        })();
    }, [load]);

    const refresh = async () => {
        setRefreshing(true);
        await load();
        setRefreshing(false);
    };

    const createLink = async () => {
        if (!landingUrl.trim() || !/^https?:\/\//i.test(landingUrl.trim())) {
            toast.error("Landing URL must start with http(s)://");
            return;
        }
        setCreating(true);
        try {
            const r = await api.affV2CreateLink(landingUrl.trim(), campaign.trim() || null);
            toast.success(`Link created · code ${r.tracking_code}`);
            setLandingUrl(""); setCampaign(""); setShowCreate(false);
            await load();
        } catch (e) {
            toast.error(e.message || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const totals = useMemo(() => ({
        clicks30d: kpis?.clicks_30d?.total_clicks ?? 0,
        convertedClicks30d: kpis?.clicks_30d?.converted_clicks ?? 0,
        conversionRate: kpis?.clicks_30d?.conversion_rate_pct ?? 0,
        conv30d: kpis?.conversions_30d?.conversions_in_window ?? 0,
        pending: kpis?.conversions_30d?.pending_commission_eur ?? 0,
        paid: kpis?.conversions_30d?.paid_commission_eur ?? 0,
    }), [kpis]);

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            {!insideV12 && <Sidebar />}
            <main className="flex-1 flex flex-col min-w-0">
                {!insideV12 && <Header />}
                <div className="flex-1 p-6 md:p-8 space-y-6 max-w-[1200px] w-full mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                                <Rocket className="h-3 w-3 text-[var(--color-secondary)]" />
                                Affiliate v2 · Phase 16b
                            </div>
                            <h1
                                data-testid="aff-v2-self-title"
                                className="text-2xl md:text-3xl font-semibold tracking-tight"
                            >
                                My affiliate program
                            </h1>
                            <p className="text-xs text-text-secondary mt-1 max-w-xl">
                                Real-time tracking of your affiliate links, clicks and commissions.
                                Commission rate: <span className="text-text-main">30%</span> ·
                                Cookie window: <span className="text-text-main">30 days</span>.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="aff-v2-self-refresh"
                                onClick={refresh}
                                disabled={refreshing || loading}
                                className="text-xs rounded-full border border-[var(--color-border)] px-3 py-1.5 text-text-secondary hover:text-text-main disabled:opacity-40 flex items-center gap-2"
                            >
                                {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                Refresh
                            </button>
                            <button
                                data-testid="aff-v2-self-create-link-btn"
                                onClick={() => setShowCreate(true)}
                                disabled={disabled}
                                className="text-xs rounded-full px-4 py-1.5 font-medium flex items-center gap-2 bg-white hover:bg-[#EDEDED] text-black disabled:opacity-40"
                            >
                                <PlusCircle className="h-3 w-3" /> Create link
                            </button>
                        </div>
                    </div>

                    {disabled ? (
                        <div
                            data-testid="aff-v2-self-disabled-banner"
                            className="rounded-2xl border border-neutral-400/30 bg-neutral-400/5 p-6 flex items-start gap-3"
                        >
                            <ShieldCheck className="h-5 w-5 text-neutral-400 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-semibold text-neutral-400">
                                    Affiliate v2 is temporarily disabled
                                </h3>
                                <p className="text-xs text-text-secondary mt-1">
                                    The affiliate v2 dashboard is currently paused by the workspace admin.
                                    Your existing links still redirect and track clicks normally — you can
                                    come back later to review clicks and conversions.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* KPI grid */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <KpiCard
                                    icon={MousePointerClick}
                                    label="Clicks · 30d"
                                    value={totals.clicks30d}
                                    hint={`${totals.convertedClicks30d} converted · ${totals.conversionRate}% CVR`}
                                    tone="primary"
                                    testid="aff-v2-self-kpi-clicks"
                                />
                                <KpiCard
                                    icon={TrendingUp}
                                    label="Conversions · 30d"
                                    value={totals.conv30d}
                                    tone="secondary"
                                    testid="aff-v2-self-kpi-conv"
                                />
                                <KpiCard
                                    icon={Wallet}
                                    label="Pending $"
                                    value={fmtEur(totals.pending)}
                                    hint="Awaiting monthly payout"
                                    tone="warning"
                                    testid="aff-v2-self-kpi-pending"
                                />
                                <KpiCard
                                    icon={Check}
                                    label="Paid $"
                                    value={fmtEur(totals.paid)}
                                    hint="Approved & transferred"
                                    tone="success"
                                    testid="aff-v2-self-kpi-paid"
                                />
                                <KpiCard
                                    icon={Activity}
                                    label="Active links"
                                    value={links.length}
                                    tone="primary"
                                    testid="aff-v2-self-kpi-links"
                                />
                            </div>

                            {/* Phase 17e · Ambassador tier progress */}
                            <TierProgressCard kind="ambassador" />

                            {/* Tabs */}
                            <div className="flex flex-wrap items-center gap-2">
                                {TABS().map((t) => (
                                    <TabBtn
                                        key={t.key}
                                        active={tab === t.key}
                                        onClick={() => setTab(t.key)}
                                        icon={t.icon}
                                        label={t.label}
                                        count={
                                            t.key === "links" ? links.length :
                                            t.key === "clicks" ? clicks.length :
                                            conversions.length
                                        }
                                        testid={`aff-v2-self-tab-${t.key}`}
                                    />
                                ))}
                            </div>

                            {/* Content */}
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                                {loading ? (
                                    <div className="p-10 flex items-center justify-center text-text-secondary text-xs gap-2">
                                        <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                    </div>
                                ) : tab === "links" ? (
                                    <LinksTable rows={links} />
                                ) : tab === "clicks" ? (
                                    <ClicksTable rows={clicks} />
                                ) : (
                                    <ConversionsTable rows={conversions} />
                                )}
                            </div>

                            <div className="text-[10px] text-text-secondary flex items-start gap-2 max-w-xl">
                                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                <span>
                                    Privacy: we store SHA256-hashed IPs only — never raw visitor IPs.
                                    Cookies are set for 30 days from the last click.
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Create link modal */}
            <AnimatePresence>
                {showCreate && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                            onClick={creating ? undefined : () => setShowCreate(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        >
                            <div
                                data-testid="aff-v2-self-create-modal"
                                className="pointer-events-auto w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden"
                            >
                                <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                    <div>
                                        <h3 className="text-base font-semibold text-text-main">Create tracking link</h3>
                                        <p className="text-xs text-text-secondary mt-1">
                                            Every click earns you 30% commission on the referred purchase within 30 days.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowCreate(false)}
                                        disabled={creating}
                                        className="text-text-secondary hover:text-text-main disabled:opacity-40"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="p-5 space-y-3">
                                    <label className="block text-xs text-text-secondary">
                                        Landing URL
                                        <input
                                            data-testid="aff-v2-self-create-url"
                                            type="url"
                                            value={landingUrl}
                                            onChange={(e) => setLandingUrl(e.target.value)}
                                            placeholder="https://globalleadsuite.com/pricing"
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main focus:outline-none focus:border-[var(--color-primary)]"
                                        />
                                    </label>
                                    <label className="block text-xs text-text-secondary">
                                        Campaign name <span className="opacity-60">(optional)</span>
                                        <input
                                            data-testid="aff-v2-self-create-campaign"
                                            type="text"
                                            value={campaign}
                                            onChange={(e) => setCampaign(e.target.value)}
                                            placeholder="e.g. spring-newsletter"
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-text-main focus:outline-none focus:border-[var(--color-primary)]"
                                        />
                                    </label>
                                </div>
                                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                    <button
                                        onClick={() => setShowCreate(false)}
                                        disabled={creating}
                                        className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={createLink}
                                        disabled={creating || !landingUrl.trim()}
                                        data-testid="aff-v2-self-create-confirm"
                                        className="text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 bg-white hover:bg-[#EDEDED] text-black disabled:opacity-40"
                                    >
                                        {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlusCircle className="h-3 w-3" />}
                                        Create link
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}


function LinksTable({ rows }) {
    if (!rows.length) {
        return (
            <div className="p-10 text-center text-xs text-text-secondary">
                No tracking links yet — create your first one to start earning commissions.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table data-testid="aff-v2-self-links-table" className="w-full text-xs">
                <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left px-4 py-3">Code</th>
                        <th className="text-left px-4 py-3">Landing</th>
                        <th className="text-left px-4 py-3">Campaign</th>
                        <th className="text-right px-4 py-3">Clicks</th>
                        <th className="text-right px-4 py-3">Created</th>
                        <th className="text-right px-4 py-3">Short URL</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {rows.map((r) => (
                        <tr key={r.tracking_code} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3 font-mono text-text-main">{r.tracking_code}</td>
                            <td className="px-4 py-3 text-text-secondary max-w-[280px] truncate">
                                <a href={r.landing_url} target="_blank" rel="noreferrer" className="hover:text-text-main flex items-center gap-1">
                                    {r.landing_url} <ExternalLink className="h-3 w-3" />
                                </a>
                            </td>
                            <td className="px-4 py-3 text-text-secondary">{r.campaign_name || "—"}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{r.click_count ?? 0}</td>
                            <td className="px-4 py-3 text-right text-text-secondary">{fmtDate(r.created_at)}</td>
                            <td className="px-4 py-3 text-right">
                                <button
                                    onClick={() => copyToClipboard(r.short_url)}
                                    data-testid={`aff-v2-self-copy-${r.tracking_code}`}
                                    className="inline-flex items-center gap-1 text-[var(--color-secondary)] hover:text-text-main"
                                >
                                    <Copy className="h-3 w-3" /> copy
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function ClicksTable({ rows }) {
    if (!rows.length) {
        return (
            <div className="p-10 text-center text-xs text-text-secondary">
                No clicks recorded yet.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table data-testid="aff-v2-self-clicks-table" className="w-full text-xs">
                <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left px-4 py-3">When</th>
                        <th className="text-left px-4 py-3">Code</th>
                        <th className="text-left px-4 py-3">Referrer</th>
                        <th className="text-left px-4 py-3">Cookie</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {rows.slice(0, 100).map((r, i) => (
                        <tr key={`${r.tracking_code}-${r.clicked_at}-${i}`} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3 text-text-secondary">{fmtDate(r.clicked_at)}</td>
                            <td className="px-4 py-3 font-mono text-text-main">{r.tracking_code}</td>
                            <td className="px-4 py-3 text-text-secondary max-w-[240px] truncate">{r.referer || "—"}</td>
                            <td className="px-4 py-3 text-text-secondary font-mono">{(r.cookie_id || "").slice(0, 12)}…</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function ConversionsTable({ rows }) {
    if (!rows.length) {
        return (
            <div className="p-10 text-center text-xs text-text-secondary">
                No conversions yet.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table data-testid="aff-v2-self-conv-table" className="w-full text-xs">
                <thead className="border-b border-[var(--color-border)] text-text-secondary uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left px-4 py-3">When</th>
                        <th className="text-left px-4 py-3">Type</th>
                        <th className="text-right px-4 py-3">Purchase $</th>
                        <th className="text-right px-4 py-3">Commission $</th>
                        <th className="text-left px-4 py-3">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {rows.map((r) => (
                        <tr key={r.id || `${r.affiliate_id}-${r.converted_at}`} className="hover:bg-white/[0.02]">
                            <td className="px-4 py-3 text-text-secondary">{fmtDate(r.converted_at)}</td>
                            <td className="px-4 py-3 text-text-main">{r.conversion_type || "—"}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-text-secondary">
                                {fmtEur(r.amount_eur)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-[var(--color-secondary)]">
                                {fmtEur(r.commission_amount_eur)}
                            </td>
                            <td className="px-4 py-3">
                                <ConversionStatusBadge status={r.status} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
